import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { BrandingProvider } from "@/contexts/BrandingContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";

// Eager load critical pages
import Index from "./pages/Index";
import Login from "./pages/Login";
import NotFound from "./pages/NotFound";

// Lazy load all other pages
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ProcessosList = lazy(() => import("./pages/ProcessosList"));
const ProcessoDetail = lazy(() => import("./pages/ProcessoDetail"));
const Prazos = lazy(() => import("./pages/Prazos"));
const ConfiguracoesIntegracoes = lazy(() => import("./pages/ConfiguracoesIntegracoes"));
const ConfiguracoesPersonalizacoes = lazy(() => import("./pages/ConfiguracoesPersonalizacoes"));
const DashboardDataJud = lazy(() => import("./pages/DashboardDataJud"));
const ImportacaoProcessos = lazy(() => import("./pages/ImportacaoProcessos"));
const CasesList = lazy(() => import("./pages/CasesList"));
const CaseDetail = lazy(() => import("./pages/CaseDetail"));
const ClienteDetail = lazy(() => import("./pages/ClienteDetail"));
const Calendario = lazy(() => import("./pages/Calendario"));
const Clientes = lazy(() => import("./pages/Clientes"));
const Contratos = lazy(() => import("./pages/Contratos"));
const Pastas = lazy(() => import("./pages/Pastas"));
const Tarefas = lazy(() => import("./pages/Tarefas"));
const Usuarios = lazy(() => import("./pages/Usuarios"));
const Faturas = lazy(() => import("./pages/Faturas"));
const AdminModelos = lazy(() => import("./pages/AdminModelos"));
const Mensagens = lazy(() => import("./pages/Mensagens"));
const PastaDetail = lazy(() => import("./pages/PastaDetail"));
const Perfil = lazy(() => import("./pages/Perfil"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Cadastro = lazy(() => import("./pages/Cadastro"));
const Verify2FA = lazy(() => import("./pages/Verify2FA"));
const ConfiguracoesNotificacoes = lazy(() => import("./pages/ConfiguracoesNotificacoes"));
const BuscaProcesso = lazy(() => import("./pages/BuscaProcesso"));
const ChangePassword = lazy(() => import("./pages/ChangePassword"));
const ImportacaoCSV = lazy(() => import("./pages/ImportacaoCSV"));

// Optimized QueryClient with better defaults for performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Keep data fresh for 5 minutes before refetching
      staleTime: 1000 * 60 * 5,
      // Keep unused data in cache for 30 minutes
      gcTime: 1000 * 60 * 30,
      // Don't refetch on window focus for less volatile data
      refetchOnWindowFocus: false,
      // Retry failed requests up to 2 times
      retry: 2,
      // Don't refetch on reconnect by default
      refetchOnReconnect: false,
    },
  },
});

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <ThemeProvider>
    <QueryClientProvider client={queryClient}>
      <BrandingProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Index />} />
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/cadastro" element={<Cadastro />} />
                <Route path="/verify-2fa" element={<Verify2FA />} />
                <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
                
                {/* Protected Routes */}
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/processos" element={<ProtectedRoute><ProcessosList /></ProtectedRoute>} />
                <Route path="/processos/:id" element={<ProtectedRoute><ProcessoDetail /></ProtectedRoute>} />
                <Route path="/busca-processo" element={<ProtectedRoute><BuscaProcesso /></ProtectedRoute>} />
                <Route path="/prazos" element={<ProtectedRoute><Prazos /></ProtectedRoute>} />
                <Route path="/calendario" element={<ProtectedRoute><Calendario /></ProtectedRoute>} />
                <Route path="/clientes" element={<ProtectedRoute><Clientes /></ProtectedRoute>} />
                <Route path="/clientes/:id" element={<ProtectedRoute><ClienteDetail /></ProtectedRoute>} />
                <Route path="/contratos" element={<ProtectedRoute><Contratos /></ProtectedRoute>} />
                <Route path="/pastas" element={<ProtectedRoute><Pastas /></ProtectedRoute>} />
                <Route path="/pastas/:id" element={<ProtectedRoute><PastaDetail /></ProtectedRoute>} />
                <Route path="/tarefas" element={<ProtectedRoute><Tarefas /></ProtectedRoute>} />
                <Route path="/usuarios" element={<ProtectedRoute><Usuarios /></ProtectedRoute>} />
                <Route path="/faturas" element={<ProtectedRoute><Faturas /></ProtectedRoute>} />
                <Route path="/mensagens" element={<ProtectedRoute><Mensagens /></ProtectedRoute>} />
                <Route path="/cases" element={<ProtectedRoute><CasesList /></ProtectedRoute>} />
                <Route path="/cases/:id" element={<ProtectedRoute><CaseDetail /></ProtectedRoute>} />
                <Route path="/admin/modelos" element={<ProtectedRoute><AdminModelos /></ProtectedRoute>} />
                <Route path="/configuracoes/integracoes" element={<ProtectedRoute><ConfiguracoesIntegracoes /></ProtectedRoute>} />
                <Route path="/configuracoes/personalizacoes" element={<ProtectedRoute><ConfiguracoesPersonalizacoes /></ProtectedRoute>} />
                <Route path="/configuracoes/notificacoes" element={<ProtectedRoute><ConfiguracoesNotificacoes /></ProtectedRoute>} />
                <Route path="/dashboard-datajud" element={<ProtectedRoute><DashboardDataJud /></ProtectedRoute>} />
                <Route path="/importacao-processos" element={<ProtectedRoute><ImportacaoProcessos /></ProtectedRoute>} />
                <Route path="/importacao-csv" element={<ProtectedRoute><ImportacaoCSV /></ProtectedRoute>} />
                <Route path="/perfil" element={<ProtectedRoute><Perfil /></ProtectedRoute>} />
                
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </BrandingProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
